import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RegistrationForm from './components/RegistrationForm';
import VisitorsManagement from './components/VisitorsManagement';
import GatePass from './components/GatePass';
import Login from './components/Login';
import './App.css';

function App() {
  // Authentication State with 24-hour token expiration check
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const expiresAt = localStorage.getItem('vms_auth_token_expires_at');
      if (expiresAt && Date.now() > Number(expiresAt)) {
        // Expired after 24 hours
        localStorage.removeItem('vms_auth_user');
        localStorage.removeItem('vms_auth_token');
        localStorage.removeItem('vms_auth_token_expires_at');
        return null;
      }
      const savedUser = localStorage.getItem('vms_auth_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const [currentPage, setCurrentPage] = useState('register');
  const [activeGatePassVisitor, setActiveGatePassVisitor] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle successful login with 24-hour expiration storage
  const handleLoginSuccess = (data) => {
    try {
      const expiresAtMs = data.expiresAt
        ? new Date(data.expiresAt).getTime()
        : Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('vms_auth_user', JSON.stringify(data.user));
      localStorage.setItem('vms_auth_token', data.token);
      localStorage.setItem('vms_auth_token_expires_at', String(expiresAtMs));
    } catch (e) {
      console.error('Error saving session:', e);
    }
    setCurrentUser(data.user);
    setSuccess(`Welcome back, ${data.user.name || 'Super Admin'}!`);
    setTimeout(() => setSuccess(''), 4000);
  };

  // Handle logout
  const handleLogout = () => {
    try {
      localStorage.removeItem('vms_auth_user');
      localStorage.removeItem('vms_auth_token');
      localStorage.removeItem('vms_auth_token_expires_at');
    } catch (e) {
      console.error('Error removing session:', e);
    }
    setCurrentUser(null);
    setActiveGatePassVisitor(null);
    setCurrentPage('register');
  };

  // Verify session on mount and ensure 24-hour token expiration
  useEffect(() => {
    const token = localStorage.getItem('vms_auth_token');
    const expiresAt = localStorage.getItem('vms_auth_token_expires_at');

    if (token) {
      if (expiresAt && Date.now() > Number(expiresAt)) {
        handleLogout();
        setError('Your 24-hour session has expired. Please sign in again.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      axios
        .get(`/api/auth/verify?token=${encodeURIComponent(token)}`)
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            handleLogout();
            setError('Your 24-hour session has expired. Please sign in again.');
            setTimeout(() => setError(''), 5000);
          }
        });
    }
  }, []);

  // Handle new visitor registration
  const handleRegistration = async (formData) => {
    try {
      const response = await axios.post('/api/visitors', formData);
      setSuccess('Visitor registered successfully! Gate pass generated.');
      setActiveGatePassVisitor(response.data);
      setTimeout(() => setSuccess(''), 4000);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to register visitor. Please check your details.';
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
      console.error(err);
      return false;
    }
  };

  // Handle pass active / inactive toggle for active pass view
  const handleTogglePassStatus = async (id, newStatus) => {
    try {
      // Disallow reactivating an already inactive pass
      if (
        activeGatePassVisitor &&
        activeGatePassVisitor._id === id &&
        activeGatePassVisitor.passStatus === 'inactive'
      ) {
        setError('This gate pass has already been deactivated and cannot be reactivated.');
        setTimeout(() => setError(''), 4000);
        return;
      }

      let shouldCheckOut = false;
      if (
        newStatus === 'inactive' &&
        activeGatePassVisitor &&
        activeGatePassVisitor._id === id &&
        !activeGatePassVisitor.checkOutTime
      ) {
        const confirmCheckOut = window.confirm(
          `Visitor "${activeGatePassVisitor.name}" has not checked out yet.\n\nTo make this pass INACTIVE, the visitor must be checked out first.\n\nWould you like to check out this visitor now and deactivate the pass?`
        );

        if (!confirmCheckOut) {
          // Cancelled: pass remains active
          return;
        }

        shouldCheckOut = true;
      }

      const now = new Date();
      const payload = { passStatus: newStatus };
      if (shouldCheckOut) {
        payload.checkOutTime = now;
      }

      // Optimistic update
      setActiveGatePassVisitor((prev) =>
        prev && prev._id === id
          ? {
            ...prev,
            passStatus: newStatus,
            ...(shouldCheckOut ? { checkOutTime: now.toISOString() } : {}),
          }
          : prev
      );

      const response = await axios.put(`/api/visitors/${id}`, payload);
      setActiveGatePassVisitor(response.data);
      if (shouldCheckOut) {
        setSuccess(`Visitor checked out and pass deactivated successfully!`);
      } else {
        setSuccess(`Pass successfully marked as ${newStatus.toUpperCase()}!`);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update pass status.');
      console.error(err);
    }
  };

  // If user is not authenticated as SuperAdmin, show Login screen only
  if (!currentUser) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      <div className="navbar">
        <div className="navbar-top-row">
          <div className="admin-session-badge">
            <span className="admin-role-tag">SuperAdmin</span>
            <span>{currentUser.email}</span>
          </div>
          <button
            className="nav-btn btn-logout"
            onClick={handleLogout}
            title="Log out of SuperAdmin session"
          >
            🚪 Sign Out
          </button>
        </div>

        <h1>📋 Visitor Management System</h1>

        <div className="nav-buttons">
          <button
            className={`nav-btn ${currentPage === 'register' ? 'active' : ''}`}
            onClick={() => {
              setActiveGatePassVisitor(null);
              setCurrentPage('register');
            }}
          >
            Register Visitor
          </button>
          <button
            className={`nav-btn ${currentPage === 'records' ? 'active' : ''}`}
            onClick={() => setCurrentPage('records')}
          >
            View Records
          </button>
        </div>
      </div>

      <div className="container">
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {currentPage === 'register' && (
          <div className="page-content">
            {activeGatePassVisitor ? (
              <GatePass
                visitor={activeGatePassVisitor}
                onNewRegistration={() => setActiveGatePassVisitor(null)}
                onViewRecords={() => setCurrentPage('records')}
                onClose={() => setActiveGatePassVisitor(null)}
                onToggleStatus={handleTogglePassStatus}
              />
            ) : (
              <RegistrationForm onRegistration={handleRegistration} />
            )}
          </div>
        )}

        {currentPage === 'records' && (
          <div className="page-content">
            <VisitorsManagement />
          </div>
        )}
      </div>
    </div>
  );
}

export default App;

